// -----------------------------------------------------------------------
// <copyright file="Constants.ts" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

export const ExternalCommand = {
  UpdateConfigurations: 'UpdateConfigurations',
  UpdateColor: 'UpdateColor',
  UpdateTheme: 'UpdateTheme',
  UpdateDeeplink: 'UpdateDeeplink',
  DiagnosticResult: 'DiagnosticResult',
  DiagnosticError: 'DiagnosticError',
  UpdateAuthToken: 'UpdateAuthToken',
  UpdateAuthError: 'UpdateAuthError',
  UpdateSignInStatus: 'UpdateSignInStatus',
};

export const KeyboardEventName = {
  ArrowUp: 'ArrowUp',
  ArrowDown: 'ArrowDown',
  Down: 'Down',
  Esc: 'Esc',
  Escape: 'Escape',
  Up: 'Up',
};

export const DiagnosticReference = {
  ProblemKey: 'IssueType',
  SessionId: 'sessionId',
  AutohealId: 'autohealId',
};

export const SMCTicketing = {
  SitePolicy: 'MBI_SSL',
  CommunityUrl: 'https://answers.microsoft.com/',
  AuthSupportAppVersion: 2102,
  AppNameForEmeraldConsumer: 'gethelp',
  AppNameForEmeraldCommercial: 'emeraldcommercial',
  ServiceAgreementLink:
    'https://support.microsoft.com/{0}/topic/microsoft-customer-support-service-agreement-3b3b6a44-f4ca-7a22-f77b-0b28f99a507a',
};

export const NPSCampaign = {
  GetUserLicensesUrl: '/api/contactus/v1/GetUserActiveLicenses',
  FeatureUserLicenses: 'GetUserLicenses',
};

export const ECS = {
  ClientApp: 'GetHelp',
  TeamName: 'GetHelpActionsSurface',
  EnabledFlag: 'EcsEnabled',
};

/**
 * The mapping table for locales without culture
 */
export const DefaultCultureMapping = new Map([
  ['af', 'af-ZA'],
  ['am', 'am-ET'],
  ['ar', 'ar-SA'],
  ['az-latn', 'az-latn-AZ'],
  ['be', 'be-BY'],
  ['bg', 'bg-BG'],
  ['bn', 'bn-BD'],
  ['ca', 'ca-ES'],
  ['cs', 'cs-CZ'],
  ['da', 'da-DK'],
  ['de', 'de-DE'],
  ['el', 'el-GR'],
  ['en', 'en-US'],
  ['es', 'es-ES'],
  ['et', 'et-EE'],
  ['eu', 'eu-ES'],
  ['fa', 'fa-IR'],
  ['fr', 'fr-FR'],
  ['fi', 'fi-FI'],
  ['fil', 'fil-PH'],
  ['gl', 'gl-ES'],
  ['he', 'he-IL'],
  ['hi', 'hi-IN'],
  ['hr', 'hr-HR'],
  ['hu', 'hu-HU'],
  ['id', 'id-ID'],
  ['is', 'is-IS'],
  ['it', 'it-IT'],
  ['ja', 'ja-JP'],
  ['kk', 'kk-KZ'],
  ['km', 'km-KH'],
  ['kn', 'kn-IN'],
  ['ko', 'ko-KR'],
  ['lo', 'lo-LA'],
  ['lt', 'lt-LT'],
  ['lv', 'lv-LV'],
  ['mk', 'mk-MK'],
  ['ml', 'ml-IN'],
  ['ms', 'ms-MY'],
  ['nb', 'nb-NO'],
  ['nl', 'nl-NL'],
  ['pl', 'pl-PL'],
  ['pt', 'pt-PT'],
  ['ro', 'ro-RO'],
  ['ru', 'ru-RU'],
  ['sk', 'sk-SK'],
  ['sl', 'sl-SI'],
  ['sq', 'sq-AL'],
  ['sr-latn', 'sr-latn-RS'],
  ['sv', 'sv-SE'],
  ['sw', 'sw-KE'],
  ['ta', 'ta-IN'],
  ['te', 'te-IN'],
  ['th', 'th-TH'],
  ['tr', 'tr-TR'],
  ['uk', 'uk-UA'],
  ['uz-latn', 'uz-latn-UZ'],
  ['vi', 'vi-VN'],
  ['zh-hans-cn', 'zh-CN'],
  ['zh-hans-sg', 'zh-CN'],
  ['zh-hant-tw', 'zh-TW'],
  ['zh-hant-hk', 'zh-HK'],
  ['zh-hant-mo', 'zh-HK'],
]);

export const GetCurrentSubFeatureNames = {
  AfterSearch: 'GetCurrentExp_BannerAfterSearch',
  AfterSearchWin11: 'GetCurrentExpWin11_BannerAfterSearch',
  AsModality: 'GetCurrentExp_BannerAsModality',
  AsModalityWin11: 'GetCurrentExpWin11_BannerAsModality',
};

export const StartupDafFeatureName = 'RunStartupDaf';
export const StartupDafName = 'GetHelpSystemStatusCollector';
export const StartupDafMitigationId = 'c5d3dcc1-3f75-4307-91e2-34e2abf93f05';
export const StartupDafIssueReference = 'cse.amplify.diagaction.gethelp.startupcheck';

export const DeviceRelatedThemeList: Array<string> = [
  'Devices and Drivers',
  'Other',
  'Network and Internet',
  'Activation',
  'Accounts, Profiles and Login (Windows)',
  'Input and Language',
  'Desktop Environment',
  'Power and Battery',
  'Security and Privacy',
  'Teams',
  'Microsoft Store',

  // There will be an effort to expand the device related theme list
  // Some of the themes below are yet to be supported
  'Apps',
  'Audio or Sound Card',
  'Battery',
  'BSOD',
  'Camera',
  'Cracked Screen and Physical Damage',
  'Device Performance',
  'Display or Monitor',
  'Dock or Docking station',
  'Files, Folders, and Online Storage',
  'Install and Update',
  'Internet Browsers',
  'Keyboard',
  'Mouse',
  'Pen or Stylus',
  'Recovery and Uninstall',
  'Startup Failures',
  'Touchpad or Trackpad',
  'Warranty and Service',
];

export const OsUpdateDeeplink = 'ms-settings:windowsupdate';

export const DiscoverOffersDeeplink = 'ms-surface-app:offers';

export const FirstVersionToShowAppBar = 2202;

export const AutoSuggestHighlightTag = '<b>';

export const AutoSuggestNumberOfResults = 5;

export const HttpServiceRetryTimes = 2;

export const InAppSupportedArticles = ['support.microsoft.com', 'support.office.com'];

export type SupportedManufacturer = {
  id: string;
  title: string;
  description: string;
  supportedNames: string[];
  supportedUrls: string[];
  supportedAppPaths: string[];
};

export const supportedManufacturers: SupportedManufacturer[] = [
  {
    id: 'OEM_hp',
    title: 'HPTitle',
    description: 'OEMModalityDescription',
    supportedNames: ['HP', 'Hewlett'],
    supportedUrls: ['https://support.hp.com/contact'],
    supportedAppPaths: ['hpsupportassistant://getassist/?launchpoint=51'],
  },
  {
    id: 'OEM_lenovo',
    title: 'LenovoTitle',
    description: 'OEMModalityDescription',
    supportedNames: ['Lenovo'],
    supportedUrls: ['https://support.lenovo.com/'],
    supportedAppPaths: ['lenovo-companion:'],
  },
  {
    id: 'OEM_dell',
    title: 'DellTitle',
    description: 'OEMModalityDescription',
    supportedNames: ['Dell'],
    supportedUrls: ['https://www.dell.com/support/home'],
    supportedAppPaths: [],
  },
  {
    id: 'OEM_asus',
    title: 'AsusTitle',
    description: 'OEMModalityDescription',
    supportedNames: ['Asus', 'ASUSTeK'],
    supportedUrls: ['https://www.asus.com/support/'],
    supportedAppPaths: [],
  },
  {
    id: 'OEM_acer',
    title: 'AcerTitle',
    description: 'OEMModalityDescription',
    supportedNames: ['Acer'],
    supportedUrls: ['https://www.acer.com/support/'],
    supportedAppPaths: [],
  },
  {
    id: 'OEM_gigabyte',
    title: 'GigabyteTitle',
    description: 'OEMModalityDescription',
    supportedNames: ['Gigabyte'],
    supportedUrls: ['https://www.gigabyte.com/Support'],
    supportedAppPaths: [],
  },
  {
    id: 'OEM_msi',
    title: 'MSITitle',
    description: 'OEMModalityDescription',
    supportedNames: ['MSI', 'Micro-Star'],
    supportedUrls: ['https://www.msi.com/support'],
    supportedAppPaths: [],
  },
  {
    id: 'OEM_huawei',
    title: 'HuaweiTitle',
    description: 'OEMModalityDescription',
    supportedNames: ['Huawei'],
    supportedUrls: ['https://consumer.huawei.com/en/worldwide/'],
    supportedAppPaths: [],
  },
  {
    id: 'OEM_toshiba',
    title: 'ToshibaTitle',
    description: 'OEMModalityDescription',
    supportedNames: ['Toshiba'],
    supportedUrls: ['https://dynabook.com/global/en/index.html'],
    supportedAppPaths: [],
  },
  {
    id: 'OEM_samsung',
    title: 'SamsungTitle',
    description: 'OEMModalityDescription',
    supportedNames: ['Samsung'],
    supportedUrls: ['https://www.samsung.com/support'],
    supportedAppPaths: [],
  },
];

/**
 * * Currently not kept inside BRS because we need it before calling GetEmeraldConfig
 * Todo: Move to BRS if possible
 * GetHelp app may send ll values without ll-cc format and needs to be mapped to ll-cc for SMC apis
 * The values in the array are corresponding mappings found ins SMC EOL
 * gl and no locales don't have mappings currently and will be mapped to en-us until supported
 */
export const GetHelpSMCCultureMapping = {
  'ar': ['ar-SA'],
  'bg': ['bg-BG'],
  'cs': ['cs-CZ'],
  'da': ['da-DK'],
  'de': ['de-DE'],
  'el': ['el-GR'],
  'es': ['es-ES'],
  'et': ['et-EE'],
  'fi': ['fi-FI'],
  'fr': ['fr-FR'],
  'gl': ['es-es'],
  'he': ['he-IL'],
  'hi': ['en-IN'],
  'hr': ['hr-HR'],
  'hu': ['hu-HU'],
  'id': ['id-ID'],
  'it': ['it-IT'],
  'ja': ['ja-JP'],
  'kk': ['ru-RU'],
  'ko': ['ko-KR'],
  'lt': ['lt-LT'],
  'lv': ['lv-LV'],
  'ms': ['en-US'],
  'nl': ['nl-NL'],
  'no': ['nb-no'],
  'pl': ['pl-PL'],
  'pt': ['pt-BR', 'pt-PT'],
  'pt-PT': ['pt-PT', 'pt-BR'],
  'ro': ['ro-RO'],
  'ru': ['ru-RU'],
  'sk': ['sk-SK'],
  'sl': ['sl-SI'],
  'sr': ['sr-Latn-RS', 'sr-Latn-CS'],
  'sv': ['sv-SE'],
  'th': ['th-TH'],
  'tr': ['tr-TR'],
  'uk': ['uk-UA'],
  'vi': ['vi-VN'],
  'zh-Hans': ['zh-CN'],
  'zh-Hant': ['zh-TW'],
};

export const VSBConsumerAlertFeature = 'VSBConsumerAlertFeature';
